const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const MEMBER_API = isLocal ? 'http://localhost:5290' : 'https://member.hirethink.ai';
const CV_API = isLocal ? 'http://localhost:5293' : 'https://cv.api.hirethink.ai';
const REPORT_API = isLocal ? 'http://localhost:5293' : 'https://report.api.hirethink.ai';

const COMPANY_ID = '019d8657-6c3c-7a29-867b-8aea2d337cad';

// --- Candidate Auth Helper ---

function getCandidateToken() {
  return localStorage.getItem('candidate_token');
}

function candidateHeaders(extra = {}) {
  const token = getCandidateToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function candidateAuthFetch(url, options = {}) {
  const token = getCandidateToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (res.status === 401) {
    // Token expired → clear and let UI handle redirect
    localStorage.removeItem('candidate_token');
    localStorage.removeItem('candidate_id');
    localStorage.removeItem('candidate_profile');
    window.dispatchEvent(new Event('candidate-logout'));
  }

  return res;
}

// --- Public Endpoints (CV API — No Auth) ---

export async function searchJobList(params = {}, skip = 0, take = 20) {
  const body = {
    title: params.title || null,
    currentLocation: params.currentLocation || null,
    searchText: params.searchText || null,
    workingType: params.workingType || null,
    experienceYear: params.experienceYear ?? null,
    age: params.age ?? null,
    educationLevelId: params.educationLevelId ?? null,
    isMillitaryCompleted: params.isMillitaryCompleted ?? null,
    companyId: COMPANY_ID,
    skip,
    take,
  };

  const res = await fetch(`${CV_API}/api/Career/SearchJobList`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error('İş arama başarısız');
  const data = await res.json();

  const items = data.data || data.items || [];
  const total = data.total || items.length;
  return { items, total };
}

export async function getJobDetailBySlug(slug) {
  const res = await fetch(`${CV_API}/api/Job/GetJobDetailBySlug?Slug=${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('İlan detayı alınamadı');
  const json = await res.json();
  return json.data || json;
}

// --- ParseSearchText (CV API — Public) ---

let parseContext = {
  previousTitle: null,
  previousLocation: null,
  previousWorkingType: null,
  previousExperienceYear: null,
  experienceAsked: false,
};

export function resetParseContext() {
  parseContext = {
    previousTitle: null,
    previousLocation: null,
    previousWorkingType: null,
    previousExperienceYear: null,
    experienceAsked: false,
  };
}

export async function parseSearchText(text, cvFile = null) {
  const formData = new FormData();
  formData.append('searchText', text);
  if (cvFile) {
    formData.append('cv', cvFile);
  }

  if (parseContext.previousTitle) formData.append('previousTitle', parseContext.previousTitle);
  if (parseContext.previousLocation) formData.append('previousLocation', parseContext.previousLocation);
  if (parseContext.previousWorkingType) formData.append('previousWorkingType', parseContext.previousWorkingType);
  if (parseContext.previousExperienceYear != null) formData.append('previousExperienceYear', parseContext.previousExperienceYear);
  if (parseContext.experienceAsked) formData.append('experienceAsked', 'true');

  const res = await fetch(`${CV_API}/api/Career/ParseSearchText`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('Metin analiz edilemedi');
  const data = await res.json();

  const d = data.data || data;
  if (d.title) parseContext.previousTitle = d.title;
  if (d.currentLocation) parseContext.previousLocation = d.currentLocation;
  if (d.workingType) parseContext.previousWorkingType = d.workingType;
  if (d.experienceYear != null) parseContext.previousExperienceYear = d.experienceYear;

  if (data.clarifyQuestion && /tecrübe|deneyim|experience|yıl/i.test(data.clarifyQuestion)) {
    parseContext.experienceAsked = true;
  }

  return data;
}

// --- CV Autofill (CV API — Public) ---

export async function cvAutofill(file) {
  const formData = new FormData();
  formData.append('Cv', file);

  const res = await fetch(`${CV_API}/api/Job/CvAutofill`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('CV analiz edilemedi');
  const json = await res.json();
  const data = json.data || json;

  return {
    name: data.name || null,
    surname: data.surname || null,
    email: data.email || null,
    phone: data.phone || null,
    title: data.title || data.position || data.jobTitle || null,
    currentLocation: data.location || data.currentLocation || data.city || null,
    experienceYear: data.experienceYear ?? data.experience ?? null,
    skills: data.skills || null,
    summary: data.summary || data.about || null,
    education: data.education || data.educationLevel || null,
    educationLevelId: data.educationLevelId ?? null,
    birthdate: data.birthdate || null,
    militaryStatus: data.militaryStatus || null,
    age: data.age ?? null,
    raw: data,
  };
}

// --- CheckFitScore (CV API) ---

export async function checkFitScore(cvFile, jobId) {
  const formData = new FormData();
  formData.append('Cv', cvFile);
  formData.append('JobId', jobId);

  const res = await candidateAuthFetch(`${CV_API}/api/Career/CheckFitScore`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('Skor hesaplanamadı');
  const json = await res.json();
  return json.data || json;
}

// --- Candidate Profile (CV API — Auth Required) ---

export async function getCandidateDetail(candidateId) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/GetCandidateDetail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: candidateId }),
  });
  if (!res.ok) throw new Error('Profil bilgileri alınamadı');
  return res.json();
}

export async function createCandidate(data) {
  const res = await candidateAuthFetch(`${CV_API}/api/Candidate/CreateCandidate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Profil kaydedilemedi');
  return res.json();
}

// --- CandidateLogin API (Members API — Public) ---

export async function googleConnect(code, redirectUri) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/GoogleConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: code, redirectUri }),
  });
  if (!res.ok) throw new Error('Google ile giriş başarısız');
  return res.json();
}

export async function facebookConnect(accessToken) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/FacebookConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: accessToken }),
  });
  if (!res.ok) throw new Error('Facebook ile giriş başarısız');
  return res.json();
}

export async function linkedInConnect(accessToken) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/LinkedInConnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: accessToken }),
  });
  if (!res.ok) throw new Error('LinkedIn ile giriş başarısız');
  return res.json();
}

export async function sendVerificationCode(email) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/SendVerificationCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Doğrulama kodu gönderilemedi');
  return res.json();
}

export async function verifyCode(code) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/VerifyCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: code }),
  });
  if (!res.ok) throw new Error('Kod doğrulanamadı');
  return res.json();
}

export async function sendMagicLink(email) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/SendMagicLink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Magic link gönderilemedi');
  return res.json();
}

export async function verifyMagicLink(token) {
  const res = await fetch(`${MEMBER_API}/api/CandidateLogin/VerifyMagicLink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error('Magic link doğrulanamadı');
  return res.json();
}

// --- Contact Form (CV API) ---

export async function sendContactForm({ name, email, phone, message }) {
  const res = await fetch(`${REPORT_API}/api/Report/SendContactForm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, phone, message }),
  });
  if (!res.ok) throw new Error('Mesaj gönderilemedi');
  return res.json();
}

// --- Job Apply (CV API) ---

export async function applyJob(fields) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  const res = await candidateAuthFetch(`${CV_API}/api/Job/ApplyJob`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) throw new Error('Başvuru gönderilemedi');
  return res.json();
}
